import React from "react";
import { useLocation } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* Settings screen (with embedded Notifications + Subscription sections). */

function planTone(plan, { cancelAtPeriodEnd = false, status = "" } = {}) {
  if (cancelAtPeriodEnd) {
    // Treat a queued cancellation as a warning state — the user is still
    // on a paid plan, but we want it visually distinct from a healthy renewal.
    return { color: "var(--warning, #b45309)", bg: "color-mix(in srgb, var(--warning, #f59e0b) 15%, transparent)", border: "color-mix(in srgb, var(--warning, #f59e0b) 35%, transparent)" };
  }
  if (status === "past_due") {
    return { color: "var(--danger-text)", bg: "var(--danger-subtle)", border: "var(--danger-border)" };
  }
  if (plan && plan !== "free") return { color: "var(--accent-soft-text)", bg: "var(--accent-subtle)", border: "var(--accent-border)" };
  return { color: "var(--text-secondary)", bg: "var(--bg-sunken)", border: "var(--border)" };
}

function statusLabel({ plan, status, cancelAtPeriodEnd }) {
  if (cancelAtPeriodEnd || status === "canceling") return "Cancelling";
  if (status === "trialing") return "Trial";
  if (status === "past_due") return "Past due";
  if (plan && plan !== "free") return "Active";
  return "Free";
}

function formatPeriodEnd(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch (e) {
    return null;
  }
}

function SubscriptionSection({ onNavigate }) {
  const userId = window.api?.auth?.readUserId?.() || null;
  // Share the global plan cache with every other screen on the page so we
  // don't hammer /billing/subscription with one GET per component mount.
  const planState = window.usePlan ? window.usePlan() : null;
  const loading = planState?.loading ?? true;
  const sub = planState?.subscription || null;
  const usage = planState?.usage || null;
  const fetchedAt = planState?.fetchedAt || null;
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");

  // Stripe Checkout redirects back here with ?billing=success — invalidate
  // the cache so the new plan shows up without a manual reload.
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const billing = url.searchParams.get("billing");
      if (billing) {
        url.searchParams.delete("billing");
        window.history.replaceState({}, "", url.toString());
        setTimeout(() => { window.bumpPlanRefresh?.(); }, 600);
      }
    } catch (e) {}
  }, []);

  // Portal opens in a new tab. When the user comes back here we want the
  // freshest plan state. visibilitychange alone is enough — the `focus`
  // event fires on every click into the window and used to thrash the API.
  React.useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!fetchedAt) return;
      if (Date.now() - fetchedAt <= 30_000) return;
      planState?.refresh?.();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchedAt, planState]);

  if (!userId) return null;

  const plan = sub?.plan || "free";
  const isPaid = plan !== "free";
  const fallbackName = plan === "solo" ? "Solo" : plan === "growth" ? "Growth" : "Free";
  const planName = sub?.plan_definition?.name || fallbackName;
  const status = sub?.status || (isPaid ? "active" : "free");
  const cancelAtPeriodEnd = !!sub?.cancel_at_period_end;
  const periodEndLabel = formatPeriodEnd(sub?.current_period_end);
  const tone = planTone(plan, { cancelAtPeriodEnd, status });
  const pillLabel = statusLabel({ plan, status, cancelAtPeriodEnd });
  const used = usage?.used ?? 0;
  const cap = usage?.cap;

  const onUpgrade = async () => {
    setError("");
    setBusy("upgrade");
    try {
      await window.api.billing.checkout({ planId: "solo" });
    } catch (e) {
      setBusy("");
      setError(e?.message || "Could not start checkout.");
    }
  };

  const onManage = async () => {
    setError("");
    setBusy("portal");
    try {
      await window.api.billing.portal();
      setBusy("");
    } catch (e) {
      setBusy("");
      setError(e?.message || "Could not open the billing portal.");
    }
  };

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">Subscription</h2>
      <p className="settings-section-desc">
        Plan, billing, and usage. Manage your card or cancel any time.
      </p>
      <div className="card" style={{ padding: 18 }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="skel" style={{ height: 18, width: "40%" }} />
            <div className="skel" style={{ height: 12, width: "62%" }} />
            <div className="skel" style={{ height: 36, width: 160, marginTop: 6, borderRadius: 8 }} />
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text-primary)" }}>
                    {planName}
                  </div>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      padding: "3px 8px",
                      borderRadius: 999,
                      background: tone.bg,
                      color: tone.color,
                      border: `1px solid ${tone.border}`,
                    }}
                  >
                    {pillLabel}
                  </span>
                </div>
                <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.55 }}>
                  {isPaid && periodEndLabel && cancelAtPeriodEnd && (
                    <>Your subscription will end on <strong>{periodEndLabel}</strong>. You keep full {planName} access until then — resume any time before that date to stay on the plan.</>
                  )}
                  {isPaid && periodEndLabel && !cancelAtPeriodEnd && (
                    <>Renews automatically on <strong>{periodEndLabel}</strong>.</>
                  )}
                  {isPaid && !periodEndLabel && (
                    <>You're on the {planName} plan.</>
                  )}
                  {plan === "free" && (
                    <>Upgrade to Solo for unlimited scans, 7-day windows, and the daily digest — or to Growth for 14-day windows, priority processing, and high-intent alerts.</>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {plan === "free" ? (
                  <>
                    {onNavigate && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => onNavigate("pricing")}
                      >
                        Compare plans
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={onUpgrade}
                      disabled={busy === "upgrade"}
                    >
                      {busy === "upgrade" ? (
                        <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} />
                      ) : (
                        <window.I.Sparkles size={12} />
                      )}
                      Upgrade · $12.99/mo
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={cancelAtPeriodEnd ? "btn btn-primary btn-sm" : "btn btn-secondary btn-sm"}
                    onClick={onManage}
                    disabled={busy === "portal"}
                    title="Opens the Stripe billing portal in a new tab"
                  >
                    {busy === "portal" ? (
                      <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} />
                    ) : cancelAtPeriodEnd ? (
                      <window.I.Refresh size={12} />
                    ) : (
                      <window.I.Settings size={12} />
                    )}
                    {cancelAtPeriodEnd ? "Resume subscription" : "Manage subscription"}
                  </button>
                )}
              </div>
            </div>

            {/* Free-plan scan usage meter — gives the user a clear "X of 6 used"
                so the lockout never comes as a surprise. */}
            {plan === "free" && cap != null && (
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 14,
                  borderTop: "1px solid var(--border-subtle)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 12.5 }}>
                  <span style={{ color: "var(--text-secondary)" }}>Manual scans this month</span>
                  <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                    {used} / {cap}
                  </span>
                </div>
                <div
                  style={{
                    marginTop: 6,
                    height: 6,
                    background: "var(--bg-sunken)",
                    borderRadius: 999,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, (used / cap) * 100)}%`,
                      height: "100%",
                      background:
                        used >= cap
                          ? "var(--danger)"
                          : used >= cap - 1
                            ? "var(--warning, #f59e0b)"
                            : "var(--accent)",
                      transition: "width 300ms ease",
                    }}
                  />
                </div>
              </div>
            )}

            {error && (
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 10px",
                  background: "var(--danger-subtle)",
                  color: "var(--danger-text)",
                  border: "1px solid var(--danger-border)",
                  borderRadius: 6,
                  fontSize: 12.5,
                }}
              >
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ title, desc, value, onChange, children }) {
  return (
    <div className="row">
      <div className="row-main">
        <div className="row-title">{title}</div>
        {desc && <div className="row-desc">{desc}</div>}
        {children}
      </div>
      <label className="switch">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
        <span className="switch-slider" />
      </label>
    </div>
  );
}

const DIGEST_TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 6; h <= 22; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? "AM" : "PM";
      const label = `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
      opts.push({ value, label });
    }
  }
  return opts;
})();

function normalizeDigestTime(value) {
  return /^\d{2}:\d{2}$/.test(value || "") ? value : "09:00";
}

function getLocalTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (e) {
    return null;
  }
}

function getUtcDigestTime(localTime) {
  const normalized = normalizeDigestTime(localTime);
  const [hours, minutes] = normalized.split(":").map(Number);
  const now = new Date();
  const localDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
    0,
    0
  );
  return `${String(localDate.getUTCHours()).padStart(2, "0")}:${String(localDate.getUTCMinutes()).padStart(2, "0")}`;
}

function NotificationsSection({ onNavigate, sectionRef, highlighted = false } = {}) {
  const planState = (window.usePlan ? window.usePlan() : null) || {
    loading: true,
    plan: "free",
    limits: { daily_digest_enabled: false },
  };
  const digestLocked = !planState?.loading && !planState?.limits?.daily_digest_enabled;
  const goPricing = () => {
    if (typeof onNavigate === "function") onNavigate("pricing");
    else {
      try { window.history.pushState({}, "", "/pricing"); window.dispatchEvent(new PopStateEvent("popstate")); }
      catch (e) { window.location.assign("/pricing"); }
    }
  };

  const [st, setSt] = React.useState({
    digest: false,
    digestTime: "09:00",
    emailTheme: "light",
  });
  const set = (k) => (v) => {
    if (k === "digest" && v && digestLocked) {
      goPricing();
      return;
    }
    setSt((s) => ({ ...s, [k]: v }));
  };

  const { user } = window.useCurrentUser();
  const storedEmail = React.useMemo(() => {
    try {
      const auth = JSON.parse(localStorage.getItem("cn_auth_v1") || "null");
      return auth?.email || "";
    } catch (e) {
      return "";
    }
  }, []);
  const recipientEmail = user?.email || storedEmail || "";
  const localTimeZone = React.useMemo(() => getLocalTimeZone(), []);
  const digestUtcTime = React.useMemo(() => getUtcDigestTime(st.digestTime), [st.digestTime]);

  // ── Preferences autosave ────────────────────────────────────────────
  // Load on mount, then debounce any change back to the server. The first
  // render after the GET hydrates `st` from the saved row; that hydration
  // tick is skipped by the autosave effect so we don't echo-save right
  // after loading.
  const userId = window.api?.auth?.readUserId?.() || null;
  const [loaded, setLoaded] = React.useState(false);
  const [hasSavedPreferences, setHasSavedPreferences] = React.useState(false);
  const skipNextSaveRef = React.useRef(true);
  const saveTimerRef = React.useRef(null);
  const [saveStatus, setSaveStatus] = React.useState("idle"); // idle|saving|saved|err

  React.useEffect(() => {
    if (!userId) {
      setLoaded(true);
      setHasSavedPreferences(false);
      skipNextSaveRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await window.api.preferences.get(userId);
        const p = resp?.preferences;
        if (!cancelled) {
          setHasSavedPreferences(!!p);
        }
        if (!cancelled && p) {
          setSt((s) => ({
            ...s,
            digest: typeof p.digest_enabled === "boolean" ? p.digest_enabled : s.digest,
            digestTime: normalizeDigestTime(p.digest_local_time || p.digest_time || s.digestTime),
            emailTheme: p.theme === "dark" ? "dark" : p.theme === "light" ? "light" : s.emailTheme,
          }));
        }
      } catch (e) {
        console.warn("Could not load notification preferences:", e);
      } finally {
        if (!cancelled) {
          setLoaded(true);
          // Allow exactly one render with hydrated state to flow through
          // before re-arming autosave.
          requestAnimationFrame(() => {
            skipNextSaveRef.current = false;
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  React.useEffect(() => {
    if (!loaded || !userId) return;
    if (skipNextSaveRef.current) return;
    if (!hasSavedPreferences && !st.digest) {
      setSaveStatus("idle");
      return;
    }

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus("saving");
    saveTimerRef.current = setTimeout(async () => {
      try {
        await window.api.preferences.update(userId, {
          email: recipientEmail || null,
          digest_enabled: st.digest,
          digest_time: digestUtcTime,
          digest_local_time: normalizeDigestTime(st.digestTime),
          digest_timezone: localTimeZone,
          theme: st.emailTheme,
        });
        setHasSavedPreferences(true);
        setSaveStatus("saved");
        setTimeout(() => {
          setSaveStatus((s) => (s === "saved" ? "idle" : s));
        }, 1500);
      } catch (e) {
        console.warn("Autosave failed:", e);
        setSaveStatus("err");
      }
    }, 400);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [digestUtcTime, hasSavedPreferences, st.digest, st.digestTime, st.emailTheme, recipientEmail, loaded, localTimeZone, userId]);

  return (
    <div
      ref={sectionRef}
      className="settings-section"
      style={{
        scrollMarginTop: 96,
        borderRadius: 16,
        padding: highlighted ? "18px 18px 20px" : 0,
        transition: "padding 220ms ease, background 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
        background: highlighted ? "color-mix(in srgb, var(--accent-subtle) 68%, transparent)" : "transparent",
        boxShadow: highlighted ? "0 0 0 1px var(--accent-border), 0 0 0 8px color-mix(in srgb, var(--accent-subtle) 44%, transparent)" : "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
        <h2 className="settings-section-title">Notifications</h2>
        <div
          aria-live="polite"
          style={{
            fontSize: 12,
            color:
              saveStatus === "err"
                ? "var(--danger-text)"
                : saveStatus === "saving"
                ? "var(--text-tertiary)"
                : saveStatus === "saved"
                ? "var(--success-text)"
                : "transparent",
            transition: "color 160ms ease",
            minHeight: 16,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {saveStatus === "saving" && "Saving…"}
          {saveStatus === "saved" && "Saved"}
          {saveStatus === "err" && "Could not save"}
        </div>
      </div>
      <p className="settings-section-desc">Where we send alerts. Nothing leaves this app until you turn it on.</p>
      <div className="card">
          <ToggleRow
            title={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                Daily digest email
                {digestLocked && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      padding: "1px 7px",
                      borderRadius: 999,
                      background: "var(--accent-subtle)",
                      color: "var(--accent-soft-text)",
                      border: "1px solid var(--accent-border)",
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: "uppercase",
                    }}
                  >
                    <window.I.Lock size={9} />
                    Solo
                  </span>
                )}
              </span>
            }
            desc={
              digestLocked ? (
                <span>
                  Upgrade to Solo to receive a daily email with your top opportunities.{" "}
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); goPricing(); }}
                    style={{ color: "var(--accent-soft-text)", fontWeight: 600 }}
                  >
                    See plans →
                  </a>
                </span>
              ) : (
                "One summary email per day with your new opportunities."
              )
            }
            value={st.digest && !digestLocked}
            onChange={set("digest")}
          >
            {st.digest && (
              <div style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
              }}>
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                  Send at (your local time)
                </span>
                {localTimeZone && (
                  <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                    {localTimeZone}
                  </span>
                )}
                <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <select
                    value={st.digestTime}
                    onChange={(e) => set("digestTime")(e.target.value)}
                    style={{
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      background: "var(--bg-sunken)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      padding: "6px 30px 6px 12px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--text-primary)",
                      cursor: "pointer",
                      outline: "none",
                      lineHeight: 1.2,
                      transition: "border-color 120ms ease, background 120ms ease",
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "var(--accent, var(--text-primary))"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "var(--border)"; }}
                  >
                    {DIGEST_TIME_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <window.I.Chevron
                    size={14}
                    style={{
                      position: "absolute",
                      right: 9,
                      pointerEvents: "none",
                      color: "var(--text-secondary)",
                    }}
                  />
                </div>
                <span style={{ fontSize: 12, color: "var(--text-secondary)", marginLeft: 6 }}>
                  Email theme
                </span>
                <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                  <select
                    value={st.emailTheme}
                    onChange={(e) => set("emailTheme")(e.target.value)}
                    style={{
                      appearance: "none",
                      WebkitAppearance: "none",
                      MozAppearance: "none",
                      background: "var(--bg-sunken)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      padding: "6px 30px 6px 12px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--text-primary)",
                      cursor: "pointer",
                      outline: "none",
                      lineHeight: 1.2,
                      transition: "border-color 120ms ease, background 120ms ease",
                    }}
                  >
                    <option value="light">Light mode</option>
                    <option value="dark">Dark mode</option>
                  </select>
                  <window.I.Chevron
                    size={14}
                    style={{
                      position: "absolute",
                      right: 9,
                      pointerEvents: "none",
                      color: "var(--text-secondary)",
                    }}
                  />
                </div>
              </div>
            )}
          </ToggleRow>
        </div>
      </div>
  );
}

function RedditConnectionSection() {
  const userId = window.api?.auth?.readUserId?.() || null;
  const [loading, setLoading] = React.useState(true);
  const [connection, setConnection] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  // Banner from the OAuth callback redirect (?reddit=connected|error).
  const [banner, setBanner] = React.useState(null);

  const refresh = React.useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const resp = await window.api.reddit.connection(userId);
      setConnection(resp?.connection || { connected: false });
    } catch (e) {
      setConnection({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Read + clear the ?reddit= flag the backend callback bounces back with.
  React.useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const status = url.searchParams.get("reddit");
      if (!status) return;
      const reason = url.searchParams.get("reddit_reason");
      url.searchParams.delete("reddit");
      url.searchParams.delete("reddit_reason");
      window.history.replaceState({}, "", url.toString());
      if (status === "connected") {
        setBanner({ kind: "success", text: "Reddit account connected." });
        refresh();
      } else {
        setBanner({
          kind: "error",
          text: reason
            ? `Could not connect Reddit: ${decodeURIComponent(reason)}`
            : "Could not connect Reddit. Please try again.",
        });
      }
    } catch (e) {}
  }, [refresh]);

  const onConnect = async () => {
    if (!userId) return;
    setError("");
    setBusy("connect");
    try {
      const resp = await window.api.reddit.startOAuth(userId, "/settings");
      if (resp?.url) window.location.assign(resp.url);
      else throw new Error("Could not start the Reddit connection.");
    } catch (e) {
      setBusy("");
      setError(e?.message || "Could not start the Reddit connection.");
    }
  };

  const onDisconnect = async () => {
    if (!userId) return;
    if (!window.confirm("Disconnect your Reddit account? You'll need to reconnect before you can post replies again.")) {
      return;
    }
    setError("");
    setBusy("disconnect");
    try {
      await window.api.reddit.disconnect(userId);
      setConnection({ connected: false });
      setBanner(null);
    } catch (e) {
      setError(e?.message || "Could not disconnect Reddit.");
    } finally {
      setBusy("");
    }
  };

  if (!userId) return null;

  const connected = !!connection?.connected;
  const username = connection?.reddit_username;

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">Reddit Connection</h2>
      <p className="settings-section-desc">
        Connect Reddit to post replies from your own account. Community Ninja
        will only post when you explicitly click Post now.
      </p>
      <div className="card" style={{ padding: 18 }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="skel" style={{ height: 18, width: "40%" }} />
            <div className="skel" style={{ height: 36, width: 160, marginTop: 6, borderRadius: 8 }} />
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                  display: "grid", placeItems: "center",
                  color: connected ? "var(--success-text)" : "var(--text-tertiary)",
                }}>
                  <img src="/assets/reddit.png" alt="Reddit" width={28} height={28} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
                    {connected ? (
                      <>Connected as u/{username || "—"}</>
                    ) : (
                      "Not connected"
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 2 }}>
                    {connected
                      ? "We'll post replies under this account only when you click Post now."
                      : "No Reddit account linked yet."}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {connected ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={onConnect}
                      disabled={busy === "connect"}
                      title="Re-run the Reddit authorization"
                    >
                      {busy === "connect" ? (
                        <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} />
                      ) : (
                        <window.I.Refresh size={12} />
                      )}
                      Reconnect
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ color: "var(--danger-text)", borderColor: "var(--danger-border)" }}
                      onClick={onDisconnect}
                      disabled={busy === "disconnect"}
                    >
                      {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={onConnect}
                    disabled={busy === "connect"}
                  >
                    {busy === "connect" ? (
                      <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} />
                    ) : null}
                    Connect Reddit
                  </button>
                )}
              </div>
         
            </div>

            {banner && (
              <div style={{
                marginTop: 14,
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 12.5,
                background: banner.kind === "success" ? "var(--success-subtle)" : "var(--danger-subtle)",
                color: banner.kind === "success" ? "var(--success-text)" : "var(--danger-text)",
                border: "1px solid",
                borderColor: banner.kind === "success" ? "var(--success-border)" : "var(--danger-border)",
              }}>
                {banner.text}
              </div>
            )}

            {error && (
              <div style={{
                marginTop: 12,
                padding: "8px 10px",
                background: "var(--danger-subtle)",
                color: "var(--danger-text)",
                border: "1px solid var(--danger-border)",
                borderRadius: 6,
                fontSize: 12.5,
              }}>
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Reply tones — built-in presets (read-only) + the user's custom voices.   */
/* The chosen default is what the Generate Response composer opens into.     */
/* ────────────────────────────────────────────────────────────────────── */

// Mirrors BUILTIN_TONES in detail-panel.jsx. Kept static here so the manager
// can show the presets alongside custom tones and let either be the default.
const BUILTIN_TONES_META = [
  { id: "helpful", label: "Helpful", desc: "Pure value — answers them, no product mention." },
  { id: "soft", label: "Soft mention", desc: "Helpful first, with one natural product mention." },
  { id: "expert", label: "Expert", desc: "Authoritative, detailed practitioner voice." },
  { id: "cta", label: "Product CTA", desc: "Recommends your product with a Markdown link." },
];

const PRODUCT_MODE_OPTIONS = [
  { id: "none", label: "Don't mention product" },
  { id: "soft", label: "Soft, disclosed mention" },
  { id: "cta", label: "Recommend with link" },
];

function productModeLabel(mode) {
  return (PRODUCT_MODE_OPTIONS.find((m) => m.id === mode) || PRODUCT_MODE_OPTIONS[1]).label;
}

const EMPTY_TONE_FORM = { name: "", instructions: "", product_mode: "soft", make_default: false };

function ReplyToneSettings() {
  const userId = window.api?.auth?.readUserId?.() || null;
  const [loading, setLoading] = React.useState(true);
  const [tones, setTones] = React.useState([]);          // custom tones
  const [defaultTone, setDefaultTone] = React.useState("helpful");
  const [editing, setEditing] = React.useState(null);    // "new" | tone id | null
  const [form, setForm] = React.useState(EMPTY_TONE_FORM);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const refresh = React.useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const resp = await window.api.reddit.replyTones.list(userId);
      setTones(resp?.tones || []);
      setDefaultTone(resp?.default || "helpful");
    } catch (e) {
      setError(e?.message || "Could not load your reply tones.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const startCreate = () => { setError(""); setForm(EMPTY_TONE_FORM); setEditing("new"); };
  const startEdit = (t) => {
    setError("");
    setForm({ name: t.name, instructions: t.instructions || "", product_mode: t.product_mode || "soft", make_default: false });
    setEditing(t.id);
  };
  const cancelForm = () => { setEditing(null); setForm(EMPTY_TONE_FORM); setError(""); };

  const save = async () => {
    if (!form.name.trim()) { setError("Give your tone a name."); return; }
    setBusy(true);
    setError("");
    try {
      if (editing === "new") {
        await window.api.reddit.replyTones.create({
          name: form.name.trim(),
          instructions: form.instructions.trim(),
          productMode: form.product_mode,
          makeDefault: form.make_default,
        });
      } else {
        await window.api.reddit.replyTones.update(editing, {
          name: form.name.trim(),
          instructions: form.instructions.trim(),
          productMode: form.product_mode,
        });
      }
      await refresh();
      cancelForm();
    } catch (e) {
      setError(e?.message || "Could not save the tone.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the "${t.name}" tone? This can't be undone.`)) return;
    setBusy(true);
    setError("");
    try {
      await window.api.reddit.replyTones.remove(t.id);
      await refresh();
    } catch (e) {
      setError(e?.message || "Could not delete the tone.");
    } finally {
      setBusy(false);
    }
  };

  const makeDefault = async (id) => {
    if (id === defaultTone) return;
    setDefaultTone(id); // optimistic
    try {
      await window.api.reddit.replyTones.setDefault(id);
    } catch (e) {
      setError(e?.message || "Could not set the default tone.");
      refresh();
    }
  };

  if (!userId) return null;

  const renderRow = (id, label, desc, custom, toneObj) => {
    const isDefault = id === defaultTone;
    return (
      <div
        key={id}
        style={{
          display: "flex", alignItems: "flex-start", gap: 12,
          padding: "12px 14px",
          border: "1px solid " + (isDefault ? "var(--accent-border)" : "var(--border-subtle)"),
          background: isDefault ? "var(--accent-subtle)" : "var(--bg-sunken)",
          borderRadius: "var(--r-lg, 12px)",
        }}
      >
        <button
          type="button"
          onClick={() => makeDefault(id)}
          title={isDefault ? "Default tone" : "Set as default"}
          style={{
            flexShrink: 0, marginTop: 2, width: 16, height: 16, borderRadius: "50%",
            border: "2px solid " + (isDefault ? "var(--accent)" : "var(--border)"),
            background: isDefault ? "var(--accent)" : "transparent",
            cursor: "pointer", padding: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>{label}</span>
            {custom ? (
              <span className="pill pill-neutral" style={{ textTransform: "none", fontWeight: 500 }}>
                {productModeLabel(toneObj.product_mode)}
              </span>
            ) : (
              <span style={{ fontSize: 11, color: "var(--text-quaternary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Built-in
              </span>
            )}
            {isDefault && (
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--accent-soft-text, var(--accent))" }}>
                DEFAULT
              </span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 3, lineHeight: 1.5 }}>
            {desc}
          </div>
        </div>
        {custom && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              className="btn btn-ghost btn-icon btn-sm"
              onClick={() => startEdit(toneObj)}
              disabled={busy}
              title="Edit tone"
            >
              <window.I.Edit size={13} />
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-icon btn-sm"
              onClick={() => remove(toneObj)}
              disabled={busy}
              title="Delete tone"
              style={{ color: "var(--danger-text)" }}
            >
              <window.I.Trash size={13} />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="settings-section">
      <h2 className="settings-section-title">Reply tone</h2>
      <p className="settings-section-desc">
        Default voice for generated replies. Add custom tones and optional product links.
      </p>
      <div className="card" style={{ padding: 18 }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="skel" style={{ height: 48, borderRadius: 12 }} />
            <div className="skel" style={{ height: 48, borderRadius: 12 }} />
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {BUILTIN_TONES_META.map((t) => renderRow(t.id, t.label, t.desc, false, null))}
              {tones.map((t) =>
                renderRow(
                  t.id, t.name,
                  (t.instructions && t.instructions.trim()) || "Custom tone.",
                  true, t,
                ),
              )}
            </div>

            {editing ? (
              <div style={{
                marginTop: 14, padding: 14,
                border: "1px solid var(--border)", borderRadius: "var(--r-lg, 12px)",
                background: "var(--bg)",
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 12 }}>
                  {editing === "new" ? "New reply tone" : "Edit reply tone"}
                </div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 5 }}>
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  maxLength={40}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Founder voice, Technical, Promote"
                  style={{
                    width: "100%", padding: "9px 11px", fontSize: 13,
                    color: "var(--text-primary)", background: "var(--bg-sunken)",
                    border: "1px solid var(--border)", borderRadius: 8, outline: "none",
                    fontFamily: "inherit", marginBottom: 12,
                  }}
                />
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 5 }}>
                  Voice instructions <span style={{ fontWeight: 400, color: "var(--text-quaternary)" }}>(optional)</span>
                </label>
                <textarea
                  value={form.instructions}
                  maxLength={800}
                  rows={3}
                  onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
                  placeholder="How should replies in this tone read? e.g. 'Blunt and technical, cite specifics, no fluff.'"
                  style={{
                    width: "100%", padding: "9px 11px", fontSize: 13, lineHeight: 1.5,
                    color: "var(--text-primary)", background: "var(--bg-sunken)",
                    border: "1px solid var(--border)", borderRadius: 8, outline: "none",
                    fontFamily: "inherit", resize: "vertical", marginBottom: 12,
                  }}
                />
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-tertiary)", marginBottom: 5 }}>
                  Your product
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {PRODUCT_MODE_OPTIONS.map((m) => {
                    const on = form.product_mode === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, product_mode: m.id }))}
                        style={{
                          padding: "6px 12px", fontSize: 12.5, fontWeight: 600,
                          borderRadius: 999, cursor: "pointer",
                          border: "1px solid " + (on ? "var(--accent)" : "var(--border)"),
                          background: on ? "var(--accent-subtle)" : "var(--bg-sunken)",
                          color: on ? "var(--accent-soft-text, var(--accent))" : "var(--text-secondary)",
                        }}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
                {editing === "new" && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "var(--text-secondary)", marginBottom: 12, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.make_default}
                      onChange={(e) => setForm((f) => ({ ...f, make_default: e.target.checked }))}
                    />
                    Make this my default tone
                  </label>
                )}
                {error && (
                  <div style={{ fontSize: 12.5, color: "var(--danger-text)", marginBottom: 10 }}>{error}</div>
                )}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={cancelForm} disabled={busy}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
                    {busy ? <window.I.Refresh size={12} style={{ animation: "spin 900ms linear infinite" }} /> : <window.I.Check size={12} />}
                    {editing === "new" ? "Create tone" : "Save changes"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <div style={{ fontSize: 12.5, color: "var(--danger-text)", marginTop: 12 }}>{error}</div>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={startCreate}
                  style={{ marginTop: 14 }}
                >
                  <window.I.Plus size={13} />
                  Add custom tone
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SettingsScreen({
  activeProfileId,
  businessProfiles = [],
  onSwitchProfile,
  onRefreshProfiles,
  onNoProfilesRemaining,
  onRefreshOpps,
  onResultsCleared,
  onNavigate,
  onLogout,
}) {
  const location = useLocation();
  const [deletingProfile, setDeletingProfile] = React.useState(false);
  const [deletingAccount, setDeletingAccount] = React.useState(false);
  const [clearingResults, setClearingResults] = React.useState(false);
  const [exportingResults, setExportingResults] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);
  const [highlightNotifications, setHighlightNotifications] = React.useState(false);
  const notificationsRef = React.useRef(null);
  const { user } = window.useCurrentUser();
  const storedEmail = React.useMemo(() => {
    try {
      const auth = JSON.parse(localStorage.getItem("cn_auth_v1") || "null");
      return auth?.email || "";
    } catch (e) {
      return "";
    }
  }, []);
  const displayEmail = user?.email || storedEmail || "sam@groupcast.app";
  const displayName = user?.name?.trim()
    || (displayEmail
      ? displayEmail.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : "Sam Reyes");
  const currentProfile = businessProfiles.find((profile) => profile.id === activeProfileId) || null;

  React.useEffect(() => {
    if (location.state?.spotlightSection !== "notifications") return;
    notificationsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightNotifications(true);
    const timer = setTimeout(() => setHighlightNotifications(false), 2200);
    return () => clearTimeout(timer);
  }, [location.state]);

  const getIdentity = async () => {
    const start = Date.now();
    while (!window.Clerk && Date.now() - start < 5000) {
      await new Promise((r) => setTimeout(r, 50));
    }
    if (window.Clerk && !window.Clerk.loaded) {
      try { await window.Clerk.load(); } catch (e) { console.warn("Clerk load failed:", e); }
    }

    const storedUserId = window.api?.auth?.readUserId?.() || null;
    const liveUserId = window.Clerk?.user
      ? (window.resolveUserId?.(window.Clerk.user) || window.Clerk.user.id)
      : null;
    let email = null;
    try {
      const auth = JSON.parse(localStorage.getItem("cn_auth_v1") || "null");
      email = auth?.email || null;
    } catch (e) {}

    return {
      userId: storedUserId || liveUserId,
      email,
    };
  };

  const onExportResults = async () => {
    if (exportingResults) return;
    const profileId = activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId) {
      window.alert("No active business profile to export.");
      return;
    }

    setExportingResults(true);
    try {
      const data = await window.api.profile.exportResults(profileId);
      const payload = data?.export || {};
      const slug = (payload?.profile?.product_name || "profile")
        .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "profile";
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `community-ninja-${slug}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Export failed:", e);
      window.alert("Could not export results: " + (e?.message || "unknown error"));
    } finally {
      setExportingResults(false);
    }
  };

  const onClearResults = async () => {
    if (clearingResults) return;
    const profileId = activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId) {
      window.alert("No active business profile to clear results for.");
      return;
    }
    const ok = window.confirm(
      "Clear all stored results for this profile? This removes opportunities, feedback, and scan history. Your profile, search plan, and subreddit radar stay. This cannot be undone."
    );
    if (!ok) return;

    setClearingResults(true);
    try {
      await window.api.profile.clearResults(profileId);
      await onRefreshOpps?.();
      onResultsCleared?.();
    } catch (e) {
      console.error("Clear results failed:", e);
      window.alert("Could not clear results: " + (e?.message || "unknown error"));
    } finally {
      setClearingResults(false);
    }
  };

  const onDeleteProfile = async () => {
    if (deletingProfile) return;
    const profileId = activeProfileId || window.api?.storage?.readProfileId?.();
    if (!profileId) {
      window.alert("No active business profile to delete.");
      return;
    }
    const ok = window.confirm(
      "Delete this business profile? This removes only the current profile and its related data. This cannot be undone."
    );
    if (!ok) return;

    setDeletingProfile(true);
    try {
      await window.api.profile.delete(profileId);
      const rows = await onRefreshProfiles?.();
      const remainingProfiles = Array.isArray(rows) ? rows : [];
      const nextProfile = remainingProfiles.find((profile) => profile.id !== profileId) || remainingProfiles[0] || null;
      if (nextProfile?.id) {
        onSwitchProfile?.(nextProfile.id);
      } else {
        onNoProfilesRemaining?.();
        onSwitchProfile?.("");
      }
    } catch (e) {
      console.error("Delete profile failed:", e);
      window.alert("Could not delete profile: " + (e?.message || "unknown error"));
    } finally {
      setDeletingProfile(false);
    }
  };

  const onDeleteAccount = async () => {
    if (deletingAccount) return;
    const ok = window.confirm(
      "Delete your account? This removes all business profiles, scans, opportunities, preferences, and account data. This cannot be undone."
    );
    if (!ok) return;

    setDeletingAccount(true);
    try {
      const { userId, email } = await getIdentity();
      await window.api.profile.deleteAccount({ userId, email });

      window.clearAppBrowserState?.();

      if (window.Clerk?.user && typeof window.Clerk.user.delete === "function") {
        try { await window.Clerk.user.delete(); } catch (e) { console.warn("Clerk delete failed:", e); }
      } else if (window.Clerk && typeof window.Clerk.signOut === "function") {
        try { await window.Clerk.signOut(); } catch (e) {}
      }

      await window.hardLogout?.({ skipClerkSignOut: true });
    } catch (e) {
      console.error("Delete account failed:", e);
      window.alert("Could not delete account: " + (e?.message || "unknown error"));
      setDeletingAccount(false);
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await onLogout?.();
    } catch (e) {
      setLoggingOut(false);
    }
  };

  return (
    <div className="page screen-enter" style={{ maxWidth: 860 }}>
      <Seo title="Settings" noindex />
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">
            Compliance, data, and account preferences.
          </p>
        </div>
      </div>

      <RedditConnectionSection />

      <ReplyToneSettings />

      <div className="settings-section">
        <h2 className="settings-section-title">What this app does and doesn't</h2>
        <p className="settings-section-desc">Read-only is a design choice, not an oversight.</p>
        <div className="card" style={{ padding: 18 }}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { yes: true, t: "We monitor public Reddit conversations for relevance to your business." },
              { yes: true, t: "We link you back to Reddit so you can read the full thread before engaging." },
              { yes: false, t: "We never post, comment, DM, or vote on your behalf." },
              { yes: false, t: "We never automate replies or schedule content to Reddit." },
              { yes: true, t: "You are responsible for following each subreddit's rules and Reddit's site-wide policies." },
            ].map((row, i) => (
              <li key={i} style={{ display: "flex", gap: 12 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                  display: "grid", placeItems: "center",
                  background: row.yes ? "var(--success-subtle)" : "var(--danger-subtle)",
                  color: row.yes ? "var(--success-text)" : "var(--danger-text)",
                  border: "1px solid",
                  borderColor: row.yes ? "var(--success-border)" : "var(--danger-border)",
                }}>
                  {row.yes ? <window.I.Check size={12} /> : <window.I.X size={12} />}
                </div>
                <div style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.55 }}>{row.t}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <SubscriptionSection onNavigate={onNavigate} />

      <NotificationsSection
        onNavigate={onNavigate}
        sectionRef={notificationsRef}
        highlighted={highlightNotifications}
      />

      <div className="settings-section">
        <h2 className="settings-section-title">Data retention</h2>
        <p className="settings-section-desc">You own your data. Take it anywhere or remove it any time.</p>
        <div className="card">
          <div className="row">
            <div className="row-main">
              <div className="row-title">Export results</div>
              <div className="row-desc">Download every opportunity, scan, and feedback signal as JSON.</div>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={onExportResults}
              disabled={exportingResults}
            >
              <window.I.Download size={13} />
              {exportingResults ? "Exporting…" : "Export"}
            </button>
          </div>
          <div className="row">
            <div className="row-main">
              <div className="row-title">Delete stored results</div>
              <div className="row-desc">Clears all opportunities and feedback. Your profile stays.</div>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={onClearResults}
              disabled={clearingResults || deletingProfile || deletingAccount}
            >
              {clearingResults ? "Clearing…" : "Clear results"}
            </button>
          </div>
          <div className="row">
            <div className="row-main">
              <div className="row-title" style={{ color: "var(--danger-text)" }}>Delete profile</div>
              <div className="row-desc">
                {currentProfile?.name
                  ? `Removes only ${currentProfile.name} and its scans, results, and saved radar.`
                  : "Removes only the current business profile and its related data."}
              </div>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              style={{ color: "var(--danger-text)", borderColor: "var(--danger-border)" }}
              onClick={onDeleteProfile}
              disabled={deletingProfile || deletingAccount}
            >
              <window.I.Trash size={13} />
              {deletingProfile ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h2 className="settings-section-title">Account</h2>
        <div className="card">
          <div className="row">
            <div className="row-main">
              <div className="row-title">{displayName}</div>
              <div className="row-desc">{displayEmail}</div>
            </div>
          </div>
          <div className="row">
            <div className="row-main">
              <div className="row-title">Log out</div>
              <div className="row-desc">End your current session on this device.</div>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleLogout}
              disabled={loggingOut || deletingAccount || deletingProfile}
            >
              {loggingOut ? (
                <window.I.Refresh size={13} style={{ animation: "spin 900ms linear infinite" }} />
              ) : (
                <window.I.LogOut size={13} />
              )}
              {loggingOut ? "Logging out…" : "Log out"}
            </button>
          </div>
          <div className="row">
            <div className="row-main">
              <div className="row-title" style={{ color: "var(--danger-text)" }}>Delete account</div>
              <div className="row-desc">Removes all business profiles, related data, and your account. Cannot be undone.</div>
            </div>
            <button
              className="btn btn-secondary btn-sm"
              style={{ color: "var(--danger-text)", borderColor: "var(--danger-border)" }}
              onClick={onDeleteAccount}
              disabled={deletingAccount || deletingProfile}
            >
              <window.I.Trash size={13} />
              {deletingAccount ? "Deleting…" : "Delete account"}
            </button>
          </div>
        </div>
      </div>

      {window.LegalFooter && <window.LegalFooter variant="settings" />}
    </div>
  );
}

window.SettingsScreen = SettingsScreen;
